import { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { ArrowLeft, Play, Plus, Trash2, Users, User, Loader2, Copy, RefreshCw, Puzzle, Heart, Sparkles, TrendingUp, Wrench, X, Link2, QrCode } from 'lucide-react'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { CreateCharacterDialog } from './CreateCharacterDialog'
import { EditCharacterDialog } from './EditCharacterDialog'
import { ClassesTab } from './ClassesTab'
import { AttributesTab } from './AttributesTab'
import { ItemsTab } from './ItemsTab'
import { AbilitiesTab } from './AbilitiesTab'
import { MissionsTab } from './MissionsTab'
import { MissionTimeline } from './MissionTimeline'
import { CollapsibleSection } from './CollapsibleSection'
import { CharacterDetailsSheet } from './CharacterDetailsSheet'
import { ModuleManagerDialog, TableModulesSection } from '@/components/modules'
import { toast } from 'sonner'
import { bookIaApi } from '@/modules/book_ia/services/bookIaApi'
import { useTables, useCharacters } from '@/hooks/useBackend'
import { apiService, getMediaUrl } from '@/services/api'
import { extractIdFromSlug, createUrlSlug } from '@/utils/slugUtils'
import { getCharacterType, getCharacterTypeStyle } from '@/utils/characterUtils'
import { cn } from '@/lib/utils'
import { canManageCampaign } from '@/utils/permissions'
import type { User as UserType, Table, Character, Member, Mission } from '@/types'

// Barra de HP compacta (usada inline no mobile e na coluna fixa do desktop).
function CharacterHpBar({ current, total }: { current: number; total: number }) {
  const pct = total > 0 ? (current / total) * 100 : 0
  const tone = pct > 50 ? 'success' : pct > 25 ? 'warning' : 'error'
  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <Heart
          className={cn(
            'h-3 w-3 fill-current',
            tone === 'success' && 'text-success',
            tone === 'warning' && 'text-warning',
            tone === 'error' && 'text-error'
          )}
        />
        <span className="text-[10px] font-bold text-muted-foreground">{current}/{total}</span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full border border-border bg-section">
        <div
          className={cn(
            'h-full rounded-full transition-all',
            tone === 'success' && 'bg-success',
            tone === 'warning' && 'bg-warning',
            tone === 'error' && 'bg-error'
          )}
          style={{ width: `${Math.max(5, pct)}%` }}
        />
      </div>
    </div>
  )
}

export function TableDetails() {
  const { tableId } = useParams<{ tableId: string }>()
  const navigate = useNavigate()

  const [table, setTable] = useState<Table | null>(null)
  const [characters, setCharacters] = useState<Character[]>([])
  const [members, setMembers] = useState<Member[]>([])
  const [gameConfig, setGameConfig] = useState<any>(null)
  const [missions, setMissions] = useState<Mission[]>([])
  // Segmento ativo da seção "Sistema de Jogo" (classes | attributes | items)
  const [systemSegment, setSystemSegment] = useState<'classes' | 'attributes' | 'items' | 'abilities'>('classes')
  // Banner "Sistema básico ativo" (mesas criadas do zero); dismiss por mesa
  const [systemBannerDismissed, setSystemBannerDismissed] = useState(true)
  // Diálogo do QR code de convite (hero)
  const [showInviteQr, setShowInviteQr] = useState(false)
  const [showCreateCharacter, setShowCreateCharacter] = useState(false)
  const [showEditCharacter, setShowEditCharacter] = useState(false)
  const [editingCharacter, setEditingCharacter] = useState<Character | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [user, setUser] = useState<UserType | null>(null)
  const [realTableId, setRealTableId] = useState<string | null>(null)
  const [selectedCharacterForDetails, setSelectedCharacterForDetails] = useState<Character | null>(null)
  const [showCharacterDetails, setShowCharacterDetails] = useState(false)
  const [showModuleManager, setShowModuleManager] = useState(false)
  const [moduleRefreshKey, setModuleRefreshKey] = useState(0)
  const [isLoadingModules, setIsLoadingModules] = useState(false)
  const [bookIaModuleActive, setBookIaModuleActive] = useState(false)
  const [bookIaConfigId, setBookIaConfigId] = useState<string | null>(null)
  const [bookIaReady, setBookIaReady] = useState(false)
  const [pendingBookEntry, setPendingBookEntry] = useState(false)
  const [pendingGameEntry, setPendingGameEntry] = useState(false)

  // Wizard de criação: enquanto o mestre não concluir a Tela 2 (configuração
  // do sistema de jogo), o /detalhes devolve para /campanhas/{id}/sistema —
  // "retorna de onde parou", em qualquer aparelho, porque o estado vive no
  // servidor (system_setup_complete). Aluno não configura, então não é
  // redirecionado (a mesa é jogável desde a criação).
  useEffect(() => {
    if (!isLoading && table && realTableId) {
      const isMaster = table.user_role === 'mestre'
      if (isMaster && table.system_setup_complete === false) {
        navigate(`/campanhas/${realTableId}/sistema`, { replace: true })
      }
    }
  }, [isLoading, table, realTableId, navigate])

  // Deep link #sistema: abre a seção Sistema de Jogo expandida e em foco
  const location = useLocation()
  const focusSystemSection = location.hash === '#sistema'

  useEffect(() => {
    if (!isLoading && focusSystemSection) {
      // Pequeno atraso para a seção montar antes do scroll
      const timer = setTimeout(() => {
        document.getElementById('section-sistema')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 150)
      return () => clearTimeout(timer)
    }
  }, [isLoading, focusSystemSection])

  // Estado persistido do banner "Sistema básico ativo" (dismiss por mesa)
  useEffect(() => {
    if (realTableId) {
      setSystemBannerDismissed(localStorage.getItem(`edurpg-sistema-banner-${realTableId}`) === '1')
    }
  }, [realTableId])

  const dismissSystemBanner = () => {
    if (realTableId) {
      localStorage.setItem(`edurpg-sistema-banner-${realTableId}`, '1')
    }
    setSystemBannerDismissed(true)
  }

  // ---- Código de acesso (hero é a única morada dele na página) ----

  // Link de convite: ingresso direto pela rota pública /convite/:code
  const inviteLink = table?.access_code ? `${window.location.origin}/convite/${table.access_code}` : ''

  // Mensagem pronta para o WhatsApp da turma (o link pelado não se explica)
  const inviteMessage = inviteLink
    ? `Convite para entrar na campanha EduRPG "${table?.name}"\nClique no link: ${inviteLink}`
    : ''

  const handleCopyAccessCode = () => {
    if (!table?.access_code) return
    navigator.clipboard.writeText(table.access_code)
    toast.success('Código copiado!')
  }

  const handleCopyInviteLink = () => {
    if (!inviteMessage) return
    navigator.clipboard.writeText(inviteMessage)
    toast.success('Convite copiado! Cole no WhatsApp da turma.')
  }

  const handleRegenerateAccessCode = async () => {
    if (!table) return
    if (!confirm('Regenerar o código? O atual (e os links/QR já enviados) deixará de funcionar.')) return
    try {
      const result = await apiService.regenerateAccessCode(table.id)
      setTable({ ...table, access_code: result.access_code })
      toast.success('Código regenerado!')
    } catch (error) {
      toast.error('Erro ao regenerar')
    }
  }

  const { getTableDetails } = useTables()
  const { createCharacter, updateCharacter, deleteCharacter } = useCharacters()

  // Carregar usuário atual
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const userData = await apiService.getCurrentUser()
        setUser(userData)
      } catch (error) {
        console.error('Erro ao carregar usuário:', error)
      }
    }

    fetchUser()
  }, [])

  // Extrair o ID real da tabela do slug na URL e carregar dados
  useEffect(() => {
    if (!tableId) return;

    const loadTableData = async () => {
      setIsLoading(true);
      try {
        const extractedId = extractIdFromSlug(tableId);
        if (!extractedId) {
          toast.error('ID da mesa inválido');
          navigate('/pagina-inicial');
          return;
        }
        setRealTableId(extractedId);

        const tableData = await getTableDetails(extractedId);
        if (!tableData) {
          toast.error('Mesa não encontrada');
          navigate('/pagina-inicial');
          return;
        }

        // Mapear tableData para incluir access_code
        const mappedTable: Table = {
          id: tableData.id,
          name: tableData.name,
          description: tableData.description,
          subject: tableData.subject,
          creator_id: tableData.creator_id,
          created_at: tableData.created_at,
          image_url: tableData.image_url,
          imageUrl: tableData.image_url,
          is_active: tableData.is_active,
          template_id: tableData.template_id,
          system_setup_complete: tableData.system_setup_complete ?? true,
          character_creation_open: tableData.character_creation_open ?? true,
          access_code: tableData.access_code,
          member_count: tableData.members?.length || 0,
          character_count: tableData.characters?.length || 0,
          user_role: tableData.user_role
        }
        setTable(mappedTable);
        setCharacters(tableData.characters || []);
        setMembers(tableData.members || []);
      } catch (error) {
        console.error('Erro ao carregar dados da mesa:', error);
        toast.error('Erro ao carregar dados da mesa');
      } finally {
        setIsLoading(false);
      }
    };

    loadTableData();
  }, [tableId, getTableDetails, navigate]);
  useEffect(() => {
    if (tableId) {
      console.log("🔍 TableDetailsRouter - Slug/ID recebido na URL:", tableId);

      // Verificar se é um UUID completo
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (uuidRegex.test(tableId)) {
        console.log("🔑 TableDetailsRouter - ID já é um UUID completo");
        setRealTableId(tableId);
        return;
      }

      // Buscar no banco de dados por mesas cujo ID termine com a parte final do slug
      // Esta parte precisa ser corrigida, mas por enquanto vamos verificar se este é o ID real
      console.log("⚠️ TableDetailsRouter - ID não é um UUID completo. Buscando nas tabelas...");

      // Importante: o ID vindo do Dashboard é o ID completo e deve ser usado como está
      setRealTableId(tableId);
    } else {
      console.error("❌ TableDetailsRouter - tableId não está disponível no useParams");
    }
  }, [tableId]);

  // Carregar detalhes da mesa
  useEffect(() => {
    if (!realTableId || !user) {
      console.log("⏳ TableDetailsRouter - Aguardando ID real da tabela ou usuário...");
      return;
    }

    console.log("🔄 TableDetailsRouter - Tentando carregar detalhes com ID:", realTableId);

    const loadTableDetails = async () => {
      try {
        setIsLoading(true)
        console.log("📡 TableDetailsRouter - Chamando API para obter detalhes da tabela:", realTableId);

        // Tentar usar o apiService diretamente em vez do hook
        let backendTable;
        try {
          console.log("🔍 TableDetailsRouter - Tentando chamar apiService.getTableDetails diretamente");
          backendTable = await apiService.getTableDetails(realTableId);
          console.log("✅ TableDetailsRouter - Chamada direta ao apiService bem-sucedida");
        } catch (apiError) {
          console.error("❌ TableDetailsRouter - Erro na chamada direta à API:", apiError);
          console.log("🔄 TableDetailsRouter - Tentando usar o hook getTableDetails como fallback");
          try {
            // Se falhar, tenta o método do hook
            backendTable = await getTableDetails(realTableId);
          } catch (hookError) {
            console.error("❌ TableDetailsRouter - Falha na chamada da API");

            // Mostrar uma mensagem clara de erro
            console.error("❌ TableDetailsRouter - Todas as tentativas de obter dados falharam");
            setIsLoading(false);
            // Defina table como null para mostrar a mensagem de erro adequada
            setTable(null);
            return;

            toast.error("Não foi possível conectar ao servidor.");
          }
        }

        console.log("Resposta da API:", backendTable);

        if (!backendTable || !backendTable.id) {
          console.error("Resposta da API inválida ou sem ID:", backendTable);
          toast.error("Erro: Dados da mesa inválidos recebidos da API");
          setIsLoading(false);
          return;
        }

        // Mapear dados do backend para frontend
        const mappedTable: Table = {
          id: backendTable.id,
          name: backendTable.name,
          description: backendTable.description,
          subject: backendTable.subject,
          creator_id: backendTable.creator_id,
          created_at: backendTable.created_at,
          image_url: backendTable.image_url || backendTable.imageUrl, // Suportar ambos os formatos
          imageUrl: backendTable.imageUrl || backendTable.image_url,   // Suportar ambos os formatos
          is_active: backendTable.is_active,
          // Interruptor da criação mediada — SEM este campo o aluno enxerga a
          // mesa como "livre" (foi o bug do teste de 2026-07-20: dois caminhos
          // de carregamento, só um mapeava o flag)
          character_creation_open: backendTable.character_creation_open ?? true,
          access_code: backendTable.access_code,
          member_count: backendTable.members?.length || 0,
          character_count: backendTable.characters?.length || 0
        }

        setTable(mappedTable)
        setCharacters(backendTable.characters || [])
        setMembers(backendTable.members || [])

        console.log("Dados da mesa carregados:", mappedTable)
        console.log("Personagens recebidos do backend:", backendTable.characters)
        console.log("Personagens definidos no estado:", backendTable.characters || [])
        console.log("Quantidade de personagens:", (backendTable.characters || []).length)
        console.log("Membros:", backendTable.members)
      } catch (error) {
        console.error('❌ TableDetailsRouter - Erro ao carregar detalhes da mesa:', error)

        // Tentar extrair mais informações do erro
        if (error instanceof Error) {
          console.error('❌ TableDetailsRouter - Mensagem de erro:', error.message)
          console.error('❌ TableDetailsRouter - Stack trace:', error.stack)
          toast.error(`Erro ao carregar detalhes da campanha: ${error.message}`)
        } else {
          toast.error('Erro ao carregar detalhes da campanha')
        }
      } finally {
        setIsLoading(false)
        console.log('⚠️ TableDetailsRouter - Estado de carregamento finalizado')
      }
    }

    loadTableDetails()
  }, [realTableId, user, getTableDetails])

  const handleCreateCharacter = async (characterData: Omit<Character, 'id' | 'tableId' | 'userId'>, imageFile?: File) => {
    if (!realTableId) return

    try {
      // Mapear os campos do formulário para o formato esperado pela API
      const apiCharacterData = {
        name: characterData.name,
        description: characterData.description,
        class_name: characterData.className,
        table_id: realTableId,
        image_url: characterData.imageUrl,
        level: characterData.level,
        hp_total: characterData.hpTotal,
        hp_current: characterData.hpCurrent,
        // Enviar atributos como um objeto estruturado
        attributes: characterData.attributes,
        points: characterData.points,
        is_npc: characterData.isNpc || false
      };

      console.log("Criando novo personagem:", apiCharacterData);
      console.log("Atributos enviados:", characterData.attributes);
      let newCharacter = await createCharacter(apiCharacterData)

      // Se tiver uma imagem, fazer upload após criar o personagem
      if (imageFile) {
        try {
          console.log("Fazendo upload da imagem do personagem:", newCharacter.id);
          const { image_url } = await apiService.uploadCharacterImage(newCharacter.id, imageFile)
          // Atualizar o personagem com a URL da imagem
          newCharacter = { ...newCharacter, imageUrl: image_url }
        } catch (uploadError) {
          console.error('Erro ao fazer upload da imagem:', uploadError)
          toast.error('Personagem criado, mas houve erro ao enviar a imagem')
        }
      }

      setCharacters(current => [...current, newCharacter])
      setShowCreateCharacter(false)
      toast.success(`Personagem "${newCharacter.name}" criado com sucesso!`)

      // Se havia uma entrada pendente no livro, continuar com o personagem recém-criado
      if (pendingBookEntry) {
        setPendingBookEntry(false)
        handleEnterBookIa(newCharacter)
      }

      // Se havia uma entrada pendente no jogo, seguir para a sessão agora que o
      // personagem existe (o mesmo Character é reusado por jogo/livro/módulos).
      if (pendingGameEntry) {
        setPendingGameEntry(false)
        goToGameSession()
      }
    } catch (error: any) {
      console.error('Erro ao criar personagem:', error)

      if (error?.status === 401 || error?.message?.includes('Token inválido')) {
        toast.error('Sua sessão expirou. Por favor, faça login novamente.', {
          action: {
            label: 'Fazer Login',
            onClick: () => navigate('/login')
          }
        })
      } else if (error?.status === 409 || error?.status === 403) {
        // Regra de negócio (personagem já existe / sem permissão): o hook
        // useCharacters já mostrou o toast com o detail do backend.
        setShowCreateCharacter(false)
        setPendingBookEntry(false)
        setPendingGameEntry(false)
      } else {
        toast.error('Erro ao criar personagem')
      }
    }
  }

  const handleEditCharacter = async (characterData: Omit<Character, 'id' | 'tableId' | 'userId'>, imageFile?: File, removeImage?: boolean) => {
    if (!realTableId || !editingCharacter) return

    try {
      // Se precisa remover a imagem existente
      if (removeImage && editingCharacter.imageUrl) {
        try {
          console.log("Removendo imagem do personagem:", editingCharacter.id);
          await apiService.deleteCharacterImage(editingCharacter.id)
        } catch (deleteError) {
          console.error('Erro ao remover imagem:', deleteError)
        }
      }

      // Campos de identidade: qualquer dono edita (aluno inclusive).
      const apiCharacterData: Record<string, any> = {
        name: characterData.name,
        description: characterData.description,
        table_id: realTableId,
        image_url: removeImage ? null : characterData.imageUrl,
      };

      // Classe/papel: em mesa mediada foi SORTEADA — o aluno não troca (403 no
      // backend); mestre sempre pode. Em livre, segue editável pelo dono.
      if (isTableMaster || creationOpen) {
        apiCharacterData.class_name = characterData.className
      }

      // Trava de build: nível, HP, pontos, atributos e tipo (NPC) só vão no
      // payload quando quem edita é mestre. Para o aluno, enviar esses campos
      // resultaria em 403 no backend — a UI já os mostra travados.
      if (isTableMaster) {
        apiCharacterData.level = characterData.level
        apiCharacterData.hp_total = characterData.hpTotal
        apiCharacterData.hp_current = characterData.hpCurrent
        apiCharacterData.attributes = characterData.attributes
        apiCharacterData.points = characterData.points
        apiCharacterData.is_npc = characterData.isNpc || false
      }

      console.log("Atualizando personagem existente:", editingCharacter.id, apiCharacterData);
      console.log("Atributos enviados:", characterData.attributes);
      let updatedCharacter = await updateCharacter(editingCharacter.id, apiCharacterData)

      // Se tiver uma nova imagem, fazer upload
      if (imageFile) {
        try {
          console.log("Fazendo upload da nova imagem do personagem:", editingCharacter.id);
          const { image_url } = await apiService.uploadCharacterImage(editingCharacter.id, imageFile)
          // Atualizar o personagem com a URL da imagem
          updatedCharacter = { ...updatedCharacter, imageUrl: image_url }
        } catch (uploadError) {
          console.error('Erro ao fazer upload da imagem:', uploadError)
          toast.error('Personagem atualizado, mas houve erro ao enviar a imagem')
        }
      }

      setCharacters(current =>
        current.map(char =>
          char.id === editingCharacter.id ? updatedCharacter : char
        )
      )
      setShowEditCharacter(false)
      setEditingCharacter(null)
      toast.success(`Personagem "${updatedCharacter.name}" atualizado com sucesso!`)
    } catch (error: any) {
      console.error('Erro ao atualizar personagem:', error)

      if (error?.status === 401 || error?.message?.includes('Token inválido')) {
        toast.error('Sua sessão expirou. Por favor, faça login novamente.', {
          action: {
            label: 'Fazer Login',
            onClick: () => navigate('/login')
          }
        })
      } else if (error?.status === 403 || error?.status === 409) {
        // Regra de negócio (ex.: aluno tentando editar atributos travados): o
        // hook useCharacters já exibiu a mensagem do backend. Padrão PR #53:
        // não empilhar um segundo toast genérico.
      } else {
        toast.error('Erro ao atualizar personagem')
      }
    }
  }

  const handleOpenEditCharacter = (character: Character) => {
    console.log('🎮 TableDetailsRouter - Abrindo modal de edição para:', character.name);
    setEditingCharacter(character)
    setShowEditCharacter(true)
  }

  const handleDeleteCharacter = async (characterId: string) => {
    const character = characters.find(c => c.id === characterId)
    if (!character) return

    // Confirmar exclusão
    if (!window.confirm(`Tem certeza que deseja excluir o personagem "${character.name}"?`)) {
      return
    }

    try {
      await deleteCharacter(characterId)
      setCharacters(current => current.filter(char => char.id !== characterId))
      toast.success(`Personagem "${character.name}" excluído com sucesso`)
    } catch (error: any) {
      console.error('Erro ao excluir personagem:', error)

      // Se for erro 401, sugerir fazer login novamente
      if (error?.status === 401 || error?.message?.includes('Token inválido')) {
        toast.error('Sua sessão expirou. Por favor, faça login novamente.', {
          action: {
            label: 'Fazer Login',
            onClick: () => navigate('/login')
          }
        })
      } else {
        toast.error('Erro ao excluir personagem')
      }
    }
  }


  const handleRemoveMember = async (memberId: string) => {
    if (!realTableId) return

    const memberToRemove = members.find(m => m.id === memberId)
    if (!memberToRemove) return

    // Guardar personagens do membro para possível rollback
    const memberCharacters = characters.filter(c => c.userId === memberToRemove.userId)

    try {
      // Primeiro atualizamos a interface para parecer mais responsiva
      setMembers(currentMembers =>
        currentMembers.filter(member => member.id !== memberId)
      )

      // Remover também os personagens do membro da interface
      setCharacters(currentCharacters =>
        currentCharacters.filter(char => char.userId !== memberToRemove.userId)
      )

      // Agora chamamos a API para realmente remover o membro (e seus personagens)
      await apiService.removeMemberFromTable(realTableId, memberId)

      const charCount = memberCharacters.length
      const charMsg = charCount > 0 ? ` e ${charCount} personagem${charCount > 1 ? 's' : ''}` : ''
      toast.success(`${memberToRemove.userFullName}${charMsg} removido${charCount > 1 ? 's' : ''} da campanha`)
    } catch (error) {
      // Se a API falhar, revertemos a alteração na interface
      console.error('Erro ao remover membro:', error)

      // Restaurar o membro e seus personagens na lista se a chamada da API falhar
      setMembers(currentMembers => [...currentMembers, memberToRemove])
      setCharacters(currentCharacters => [...currentCharacters, ...memberCharacters])

      toast.error('Erro ao remover participante da campanha')
    }
  }

  // Navega para a sessão de jogo da mesa (mesmo personagem usado no livro/módulos).
  const goToGameSession = () => {
    if (!table || !realTableId) return;
    const tableSlug = `${createUrlSlug(table.name)}-${realTableId}`;
    navigate(`/game/${tableSlug}`);
  }

  const handleNavigateToGame = () => {
    if (!table || !realTableId || !user) return;

    // Verificar se é mestre ou se tem personagem
    const isMaster = members.some(m => m.userId === user.id && m.role === 'mestre');
    const hasCharacter = characters.some(c => c.userId === user.id);

    if (!isMaster && !hasCharacter) {
      // Criação mediada: o aluno ENTRA sem personagem — o sorteio (rito de
      // entrada) acontece dentro da sessão, conduzido pelo mestre. Até lá o
      // chat fica mudo (regra no servidor).
      if (!creationOpen) {
        toast.info('Você entrará sem personagem — aguarde o mestre sortear o seu.');
        goToGameSession();
        return;
      }
      // Sem personagem: abrir o painel de criação e, ao criar, seguir para o
      // jogo. Mesmo padrão do livro interativo (pendingBookEntry) — o
      // personagem criado é o mesmo usado por jogo, livro e demais módulos.
      setPendingGameEntry(true);
      setShowCreateCharacter(true);
      return;
    }

    goToGameSession();
  }

  const handleEnterBookIa = async (
    characterOverride?: Character,
    options?: { forceNew?: boolean },
  ) => {
    if (!realTableId || !user) return
    const forceNew = options?.forceNew === true

    try {
      // a) achar o personagem do jogador (não-NPC)
      const playerChar = characterOverride || characters.find(c => c.userId === user.id && !c.isNpc)

      if (!playerChar) {
        // Criação mediada: o ALUNO não cria — o personagem dele nasce por
        // sorteio dentro da sessão. O mestre cria quando quiser.
        if (!creationOpen && !isTableMaster) {
          toast.info('Seu personagem nasce por sorteio na sessão de jogo. Entre no jogo e aguarde o mestre.')
          return
        }
        // Abrir dialog de criação de personagem; marcar flag para continuar após criação
        setPendingBookEntry(true)
        setShowCreateCharacter(true)
        return
      }

      // b) tentar reusar sessão in_progress nesta config (pulado no reinício)
      let sessionId: string | null = null
      if (!forceNew && bookIaConfigId) {
        try {
          const mySessions = await bookIaApi.getMySessions()
          const existing = mySessions.find(
            s => s.config_id === bookIaConfigId && s.status === 'in_progress'
          )
          if (existing) {
            sessionId = existing.id
          }
        } catch {
          // se falhar em listar, prossegue para criar nova
        }
      }

      // c) criar nova sessão se não houver existente (ou se for reinício).
      // restart=forceNew: arquiva (abandoned) os livros in_progress anteriores
      // deste jogador, preservando o conteúdo.
      if (!sessionId) {
        toast.loading(forceNew ? 'Reiniciando o livro...' : 'Abrindo livro interativo...')
        const newSession = await bookIaApi.createSession({
          table_id: realTableId,
          character_id: playerChar.id,
          restart: forceNew,
        })
        toast.dismiss()
        sessionId = newSession.id
      }

      // d) navegar
      navigate(`/mesa/${realTableId}/book_ia/${sessionId}`)
    } catch (error: any) {
      toast.dismiss()
      console.error('Erro ao entrar no livro interativo:', error)
      const errorMsg = error?.message || 'Erro ao abrir o livro interativo'
      toast.error(errorMsg)
    }
  }

  // Reinício de teste: começa uma nova partida do zero. O livro anterior é
  // arquivado (status -> abandoned), não apagado — o conteúdo é preservado.
  const handleRestartBookIa = () => {
    if (
      !window.confirm(
        'Reiniciar o livro? Uma nova partida começa do zero.\n\nO livro anterior NÃO é apagado: ele é arquivado e continua visível para o professor.',
      )
    )
      return
    handleEnterBookIa(undefined, { forceNew: true })
  }

  const handleBack = () => {
    navigate('/pagina-inicial')
  }

  // Carregar game_config quando a mesa for carregada
  useEffect(() => {
    if (!realTableId) return;

    const loadGameConfig = async () => {
      try {
        console.log('🎮 TableDetailsRouter - Carregando game_config para mesa:', realTableId);
        const config = await apiService.getGameConfig(realTableId);
        console.log('🎮 TableDetailsRouter - game_config recebido:', config);
        console.log('🎮 TableDetailsRouter - Quantidade de classes:', config?.classes?.length || 0);
        setGameConfig(config);
      } catch (error) {
        console.error('❌ Erro ao carregar game_config:', error);
      }
    };

    loadGameConfig();
  }, [realTableId]);

  // Carregar missões da mesa (capítulos da campanha)
  useEffect(() => {
    if (!realTableId) return;
    apiService
      .getMissions(realTableId)
      .then(setMissions)
      .catch((error) => console.error('Erro ao carregar missões:', error));
  }, [realTableId]);

  // Carregar módulos da mesa (detecta book_ia ativo e prontidão)
  useEffect(() => {
    if (!realTableId) return

    const loadModules = async () => {
      try {
        setIsLoadingModules(true)

        const modulesResponse = await apiService.request<{ modules: any[] }>(`/modules/table/${realTableId}`, {
          method: 'GET'
        })

        // Detectar book_ia ativo e verificar prontidão
        const bookIaModule = modulesResponse.modules?.find(
          (m: any) => m.module_id === 'book_ia' && m.is_active
        )
        setBookIaModuleActive(!!bookIaModule)

        if (bookIaModule) {
          try {
            // Prontidão via endpoint acessível a QUALQUER membro da mesa (aluno
            // inclusive). getConfigByTable/validateConfig são só-de-professor e
            // davam 403 p/ aluno, escondendo o botão. getPlayable devolve
            // {ready, config_id} sem expor a config/chave. config_id é necessário
            // p/ reabrir o livro existente.
            const playable = await bookIaApi.getPlayable(realTableId)
            setBookIaConfigId(playable.config_id)
            setBookIaReady(playable.ready)
          } catch {
            setBookIaConfigId(null)
            setBookIaReady(false)
          }
        } else {
          setBookIaConfigId(null)
          setBookIaReady(false)
        }
      } catch (error) {
        console.error('Erro ao carregar módulos:', error)
        setBookIaModuleActive(false)
        setBookIaReady(false)
      } finally {
        setIsLoadingModules(false)
      }
    }

    loadModules()
  }, [realTableId, moduleRefreshKey])

  // Função para recarregar game_config
  const reloadGameConfig = async () => {
    if (!realTableId) return;
    try {
      const config = await apiService.getGameConfig(realTableId);
      setGameConfig(config);
    } catch (error) {
      console.error('Erro ao recarregar game_config:', error);
    }
  };

  // Função para salvar classe (criar ou editar)
  const handleSaveClass = async (classData: any) => {
    if (!realTableId) return;

    try {
      if (classData.id && gameConfig?.classes?.some((c: any) => c.id === classData.id)) {
        // Atualizar classe existente
        await apiService.updateGameConfigClass(realTableId, classData.id, classData);
      } else {
        // Criar nova classe
        await apiService.createGameConfigClass(realTableId, classData);
      }
    } catch (error) {
      console.error('Erro ao salvar classe:', error);
      throw error;
    }
  };

  // Função para excluir classe
  const handleDeleteClass = async (classId: string) => {
    if (!realTableId) return;

    try {
      await apiService.deleteGameConfigClass(realTableId, classId);
    } catch (error) {
      console.error('Erro ao excluir classe:', error);
      throw error;
    }
  };

  // Função para salvar atributo (criar ou editar)
  const handleSaveAttribute = async (attributeData: any) => {
    if (!realTableId) return;

    try {
      if (attributeData.id && gameConfig?.attributes?.some((a: any) => a.id === attributeData.id)) {
        await apiService.updateGameConfigAttribute(realTableId, attributeData.id, attributeData);
      } else {
        await apiService.createGameConfigAttribute(realTableId, attributeData);
      }
    } catch (error) {
      console.error('Erro ao salvar atributo:', error);
      throw error;
    }
  };

  // HP inicial da mesa (fase 3 do plano de feedback)
  const handleSaveInitialHp = async (initialHp: number) => {
    if (!realTableId) return;
    await apiService.updateGameConfigSettings(realTableId, { initial_hp: initialHp });
  };

  // Função para excluir atributo
  const handleDeleteAttribute = async (attributeId: string) => {
    if (!realTableId) return;

    try {
      await apiService.deleteGameConfigAttribute(realTableId, attributeId);
    } catch (error) {
      console.error('Erro ao excluir atributo:', error);
      throw error;
    }
  };

  // Função para salvar item do catálogo (criar ou editar)
  const handleSaveItem = async (itemData: any) => {
    if (!realTableId) return;

    try {
      if (itemData.id && gameConfig?.items?.some((i: any) => i.id === itemData.id)) {
        await apiService.updateGameConfigItem(realTableId, itemData.id, itemData);
      } else {
        await apiService.createGameConfigItem(realTableId, itemData);
      }
    } catch (error) {
      console.error('Erro ao salvar item:', error);
      throw error;
    }
  };

  // Função para excluir item do catálogo
  const handleDeleteItem = async (itemId: string) => {
    if (!realTableId) return;

    try {
      await apiService.deleteGameConfigItem(realTableId, itemId);
    } catch (error) {
      console.error('Erro ao excluir item:', error);
      throw error;
    }
  };

  // Habilidades do catálogo (fase 5 do plano de feedback; espelho dos itens)
  const handleSaveAbility = async (abilityData: any) => {
    if (!realTableId) return;

    try {
      if (abilityData.id && gameConfig?.abilities?.some((a: any) => a.id === abilityData.id)) {
        await apiService.updateGameConfigAbility(realTableId, abilityData.id, abilityData);
      } else {
        await apiService.createGameConfigAbility(realTableId, abilityData);
      }
    } catch (error) {
      console.error('Erro ao salvar habilidade:', error);
      throw error;
    }
  };

  const handleDeleteAbility = async (abilityId: string) => {
    if (!realTableId) return;

    try {
      await apiService.deleteGameConfigAbility(realTableId, abilityId);
    } catch (error) {
      console.error('Erro ao excluir habilidade:', error);
      throw error;
    }
  };

  // === Missões (capítulos da campanha) ===
  const loadMissions = async () => {
    if (!realTableId) return;
    try {
      setMissions(await apiService.getMissions(realTableId));
    } catch (error) {
      console.error('Erro ao carregar missões:', error);
    }
  };

  const handleCreateMission = async (data: { title: string; description: string }) => {
    if (!realTableId) return;
    await apiService.createMission(realTableId, data);
    await loadMissions();
  };

  const handleUpdateMission = async (missionId: string, data: { title: string; description: string }) => {
    if (!realTableId) return;
    await apiService.updateMission(realTableId, missionId, data);
    await loadMissions();
  };

  const handleReorderMission = async (missionId: string, order: number) => {
    if (!realTableId) return;
    await apiService.updateMission(realTableId, missionId, { order });
    await loadMissions();
  };

  const handleActivateMission = async (missionId: string) => {
    if (!realTableId) return;
    await apiService.activateMission(realTableId, missionId);
    await loadMissions();
  };

  const handleCloseMission = async (missionId: string) => {
    if (!realTableId) return;
    await apiService.closeMission(realTableId, missionId);
    await loadMissions();
  };

  const handleDeleteMission = async (missionId: string) => {
    if (!realTableId) return;
    await apiService.deleteMission(realTableId, missionId);
    await loadMissions();
  };

  // Calcular estatísticas (deve estar antes dos returns condicionais)
  const stats = useMemo(() => {
    const averageLevel = characters.length > 0
      ? characters.reduce((sum, char) => sum + char.level, 0) / characters.length
      : 0

    return {
      membersCount: members.length,
      classesCount: gameConfig?.classes?.length || 0,
      charactersCount: characters.length,
      averageLevel
    }
  }, [members.length, gameConfig?.classes?.length, characters.length, characters])

  const bookIaModuleAvailable = useMemo(() => {
    if (isLoadingModules) return false
    return bookIaModuleActive && bookIaReady
  }, [bookIaModuleActive, bookIaReady, isLoadingModules])

  // Função de scroll suave (deve estar antes dos returns condicionais)
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  // Função para abrir detalhes do personagem
  const handleShowCharacterDetails = (character: Character) => {
    setSelectedCharacterForDetails(character)
    setShowCharacterDetails(true)
  }

  if (isLoading || !user) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 px-4 text-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <div>
          <h3 className="text-lg font-black uppercase italic tracking-tighter">Carregando campanha</h3>
          <p className="mt-1 text-sm font-medium text-muted-foreground">
            Aguarde enquanto obtemos os dados...
          </p>
        </div>
      </div>
    )
  }

  if (!table) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 px-4 text-center">
        <div className="rounded-full border border-border bg-section p-6">
          <User className="h-12 w-12 text-muted-foreground" />
        </div>
        <h2 className="text-2xl font-black uppercase italic tracking-tighter">Campanha não encontrada</h2>
        <p className="max-w-md font-medium text-muted-foreground">
          O link pode estar incorreto ou a campanha pode ter sido removida.
        </p>
        <Button onClick={handleBack} className="mt-2 font-bold uppercase tracking-wide">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Voltar às suas campanhas
        </Button>
      </div>
    )
  }

  const isGameMaster = canManageCampaign(user, table)

  // Mestre da mesa = criador OU membro com papel 'mestre'. Espelha o
  // check_is_table_master do backend; define quem pode editar atributos/nível
  // (trava de build) e quem vê o dialog de edição em modo completo.
  const isTableMaster = isGameMaster || members.some(m => m.userId === user.id && m.role === 'mestre')

  // Interruptor de criação de personagens (fase 2). Default aberto; só o mestre
  // alterna. Fechado: aluno não vê o botão de criar e recebe aviso nos fluxos.
  const creationOpen = table?.character_creation_open !== false

  const handleToggleCreation = async (open: boolean) => {
    if (!realTableId) return
    // Otimista: reflete já; reverte se a API falhar.
    setTable(prev => prev ? { ...prev, character_creation_open: open } : prev)
    try {
      await apiService.setCharacterCreationOpen(realTableId, open)
      toast.success(open ? 'Criação livre: alunos podem criar personagem' : 'Criação mediada: só o mestre libera a criação')
    } catch (error) {
      setTable(prev => prev ? { ...prev, character_creation_open: !open } : prev)
      toast.error('Não foi possível alterar a criação de personagens')
    }
  }

  // O sorteio da criação mediada acontece DENTRO da sessão de jogo (cerimônia
  // conduzida pelo mestre na lista de jogadores) — não há mais painel no hub.

  // Função helper para verificar se o usuário pode editar/excluir um personagem
  const canEditCharacter = (character: Character): boolean => {
    // Pode editar se:
    // 1. É o dono do personagem
    // 2. É o criador da mesa (isGameMaster)
    // 3. É mestre por papel (member.role === 'mestre')
    const isOwner = character.userId === user.id

    return isOwner || isTableMaster
  }

  return (
    <div className="min-h-screen">
      {/* ===== HERO (cinema-dark; a tonalidade segue o tema da conta) ===== */}
      <div className="relative overflow-hidden bg-hero text-white">
        <div className="pointer-events-none absolute inset-0">
          {(table.imageUrl || table.image_url) ? (
            <div className="absolute inset-0 opacity-25">
              <img
                src={getMediaUrl(table.imageUrl || table.image_url)}
                alt=""
                className="h-full w-full object-cover"
              />
            </div>
          ) : (
            <div className="hero-glow" aria-hidden="true" />
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-hero/30 via-hero/70 to-hero" />
          <div className="absolute inset-0 hidden bg-gradient-to-r from-hero via-transparent to-hero/50 sm:block" />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-4 py-6 sm:px-6 sm:py-8 lg:px-12">
          <Button
            variant="ghost"
            onClick={handleBack}
            className="-ml-3 mb-6 gap-2 text-xs font-bold uppercase tracking-widest text-white/50 hover:bg-white/10 hover:text-white sm:mb-8"
          >
            <ArrowLeft className="h-4 w-4" /> Voltar ao dashboard
          </Button>

          <div className="mb-8 flex flex-col gap-6 lg:mb-10 lg:flex-row lg:items-end lg:justify-between lg:gap-8">
            <div className="max-w-3xl space-y-4 sm:space-y-6">
              <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                <Badge variant="outline" className="border-white/20 bg-white/5 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-white/80">
                  Sistema RPG
                </Badge>
                {table.subject && (
                  <Badge variant="outline" className="border-white/10 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-white/50">
                    {table.subject}
                  </Badge>
                )}
                <Badge className={cn(
                  'rounded-full border-none px-3 py-1 text-[10px] font-black uppercase tracking-widest',
                  table.is_active ? 'bg-success/20 text-success' : 'bg-white/10 text-white/50'
                )}>
                  <span className={cn(
                    'mr-1.5 inline-block h-1.5 w-1.5 rounded-full',
                    table.is_active ? 'bg-success' : 'bg-white/40'
                  )} />
                  {table.is_active ? 'Ativa' : 'Pausada'}
                </Badge>
              </div>

              <h1 className="text-3xl font-black uppercase italic leading-[0.95] tracking-[-0.035em] drop-shadow-2xl sm:text-5xl lg:text-6xl">
                {table.name}
              </h1>

              {table.description && (
                <p className="max-w-2xl border-l-2 border-hero-accent pl-4 text-sm font-medium leading-relaxed text-white/60 sm:pl-6 sm:text-lg">
                  {table.description}
                </p>
              )}
            </div>

            {/* Código de acesso no hero (única morada do código na página,
                decisão 2026-07-22): copiar código, copiar link de convite,
                QR e regenerar. Só o mestre vê. */}
            {isGameMaster && (
              <div className="border-b border-white/10 pb-3 lg:text-right">
                <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.2em] text-white/40">
                  Código de acesso da turma
                </div>
                <div className="flex flex-wrap items-center gap-3 lg:justify-end">
                  <span className="break-all font-mono text-2xl font-black tracking-[0.15em] text-hero-accent sm:text-3xl">
                    {table.access_code || 'N/A'}
                  </span>
                  <div className="flex gap-2">
                    {([
                      { title: 'Copiar código', icon: Copy, onClick: handleCopyAccessCode },
                      { title: 'Copiar link de convite (WhatsApp)', icon: Link2, onClick: handleCopyInviteLink },
                      { title: 'Mostrar QR code', icon: QrCode, onClick: () => setShowInviteQr(true) },
                      { title: 'Regenerar código (o atual deixa de funcionar)', icon: RefreshCw, onClick: handleRegenerateAccessCode },
                    ] as const).map(({ title, icon: ActionIcon, onClick }) => (
                      <button
                        key={title}
                        type="button"
                        title={title}
                        disabled={!table.access_code}
                        onClick={onClick}
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-white/20 text-white/60 transition-colors hover:border-white/50 hover:text-white disabled:opacity-40"
                      >
                        <ActionIcon className="h-4 w-4" />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Ações */}
          <div className="grid max-w-2xl grid-cols-1 gap-3 sm:gap-4 md:grid-cols-2">
            <button
              onClick={handleNavigateToGame}
              className="group relative h-24 overflow-hidden rounded-xl bg-card text-foreground shadow-2xl transition-all active:scale-[0.99] sm:h-28"
            >
              <div className="absolute bottom-0 left-0 flex w-full items-center justify-between p-4 sm:p-6">
                <div className="text-left">
                  <span className="mb-1 block text-[10px] font-black uppercase tracking-[0.25em] text-muted-foreground transition-colors group-hover:text-foreground">
                    Sessão de jogo
                  </span>
                  <span className="inline-block text-xl font-black uppercase italic tracking-tighter transition-transform duration-300 group-hover:scale-105 sm:text-2xl">
                    Iniciar Jogo
                  </span>
                </div>
                <div className="flex h-11 w-11 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-inner transition-all group-hover:brightness-110 sm:h-12 sm:w-12">
                  <Play className="h-5 w-5 fill-current sm:h-6 sm:w-6" />
                </div>
              </div>
            </button>

            {bookIaModuleAvailable && (
              <div className="flex flex-col gap-2">
                {/* Painel do livro: OURO FIXO (--gold*), nao segue o tema da conta */}
                <button
                  onClick={() => handleEnterBookIa()}
                  className="group relative h-24 overflow-hidden rounded-xl border border-gold-dark/60 backdrop-blur-sm transition-all hover:border-gold active:scale-[0.99] sm:h-28"
                  style={{ background: 'linear-gradient(135deg, #1c1608, var(--gold-surface))' }}
                >
                  <div className="absolute right-0 top-0 rotate-12 p-6 text-gold opacity-10 transition-all duration-500 group-hover:rotate-0 group-hover:scale-110">
                    <Sparkles className="h-20 w-20 fill-current sm:h-24 sm:w-24" />
                  </div>

                  <div className="absolute bottom-0 left-0 flex w-full items-center justify-between p-4 sm:p-6">
                    <div className="text-left">
                      <span className="mb-1 block text-[10px] font-black uppercase tracking-[0.25em] text-gold transition-colors group-hover:text-gold-light">
                        Livro interativo
                      </span>
                      <span className="text-xl font-black uppercase italic tracking-tighter text-gold-light transition-colors group-hover:text-gold-hover sm:text-2xl">
                        Entrar no livro
                      </span>
                    </div>
                    <div className="flex h-11 w-11 items-center justify-center rounded-full border border-gold-dark text-gold transition-all group-hover:border-gold group-hover:text-gold-light sm:h-12 sm:w-12">
                      <Sparkles className="h-5 w-5 sm:h-6 sm:w-6" />
                    </div>
                  </div>
                </button>
                <button
                  onClick={handleRestartBookIa}
                  title="Começa uma nova partida do zero. O livro anterior é arquivado (conteúdo preservado), não apagado."
                  className="inline-flex items-center gap-1.5 self-start text-[11px] font-bold uppercase tracking-[0.15em] text-gold/60 transition-colors hover:text-gold"
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                  Reiniciar (novo livro)
                </button>
              </div>
            )}
          </div>

          {isGameMaster && (
            <div className="mt-6">
              <button
                onClick={() => setShowModuleManager(true)}
                className="group flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-3 backdrop-blur-sm transition-all hover:border-white/25 hover:bg-white/10 sm:px-5"
              >
                <Puzzle className="h-5 w-5 text-white/50 transition-colors group-hover:text-white" />
                <span className="text-sm font-bold uppercase tracking-widest text-white/50 transition-colors group-hover:text-white">
                  Gerenciar módulos
                </span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ===== CONTENT AREA (light, tema da conta) ===== */}
      <div className="min-h-screen bg-page">
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 sm:py-12 lg:px-12">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 lg:gap-8">

            {/* ===== MAIN CONTENT (8 cols) ===== */}
            <div className="space-y-6 lg:col-span-8">

      {/* Seção Missões — capítulos da campanha. Visível a todos os membros
          (jogadores veem as não-rascunho); só o mestre gerencia.
          docs/PLANO_MISSOES.md */}
      <CollapsibleSection
        id="section-missoes"
        title="Missões"
        description="Capítulos da campanha e a missão ativa"
        defaultOpen={false}
      >
        <MissionsTab
          missions={missions}
          isGameMaster={isGameMaster}
          onCreate={handleCreateMission}
          onUpdate={handleUpdateMission}
          onReorder={handleReorderMission}
          onActivate={handleActivateMission}
          onClose={handleCloseMission}
          onDelete={handleDeleteMission}
        />
      </CollapsibleSection>

      {/* Linha do tempo — só aparece quando a campanha já tem missões (I1).
          Revisitável por todos; marcos de sessão e visualizador somente leitura.
          docs/PLANO_MISSOES.md */}
      {missions.length > 0 && user && (
        <CollapsibleSection
          id="section-timeline"
          title="Linha do tempo"
          description="Revisite as missões e as sessões de jogo"
          defaultOpen={false}
        >
          <MissionTimeline
            tableId={realTableId || ''}
            missions={missions}
            isGameMaster={isGameMaster}
            currentUserId={user.id}
            characters={characters}
            members={members}
            onSessionSummarySaved={() => {
              if (realTableId) apiService.getMissions(realTableId).then(setMissions).catch(() => {})
            }}
          />
        </CollapsibleSection>
      )}

      {/* Seção Sistema de Jogo — classes, atributos e itens num só lugar,
          alternados por controle segmentado (dois níveis de navegação nunca
          usam o mesmo componente visual; docs/PLANO_CRIACAO_CAMPANHA.md) */}
      {isGameMaster && (
        <>
          {/* Banner para mesas criadas "do zero": sistema básico ativo */}
          {table?.template_id === 'custom' && !systemBannerDismissed && (
            <div className="flex items-start gap-3 rounded-xl border border-primary/40 bg-accent p-4">
              <Wrench className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
              <div className="text-sm leading-relaxed text-accent-foreground">
                <p className="font-bold">Sistema básico ativo</p>
                <p>
                  Sua campanha já está jogável com 1 classe e 3 atributos genéricos.
                  Personalize na seção Sistema de Jogo abaixo antes de convidar a turma.
                </p>
              </div>
              <button
                type="button"
                aria-label="Dispensar aviso"
                onClick={dismissSystemBanner}
                className="ml-auto rounded p-1 text-muted-foreground transition-colors hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}

          <CollapsibleSection
            id="section-sistema"
            title="Sistema de Jogo"
            description="Classes, atributos, itens e habilidades da campanha"
            defaultOpen={focusSystemSection}
          >
            <div className="mb-4 flex gap-1 rounded-lg bg-section p-1" role="tablist" aria-label="Sistema de jogo">
              {([
                ['classes', 'Classes'],
                ['attributes', 'Atributos'],
                ['items', 'Itens'],
                ['abilities', 'Habilidades'],
              ] as const).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  role="tab"
                  aria-selected={systemSegment === value}
                  onClick={() => setSystemSegment(value)}
                  className={cn(
                    'flex-1 rounded-md py-2 text-xs font-bold uppercase tracking-widest transition-colors',
                    systemSegment === value
                      ? 'bg-card text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  )}
                >
                  {label}
                </button>
              ))}
            </div>

            {systemSegment === 'classes' && (
              <ClassesTab
                tableId={realTableId || ''}
                classes={gameConfig?.classes || []}
                isGameMaster={isGameMaster}
                onClassesChange={reloadGameConfig}
                onSaveClass={handleSaveClass}
                onDeleteClass={handleDeleteClass}
              />
            )}

            {systemSegment === 'attributes' && (
              <AttributesTab
                tableId={realTableId || ''}
                attributes={gameConfig?.attributes || []}
                isGameMaster={isGameMaster}
                onAttributesChange={reloadGameConfig}
                onSaveAttribute={handleSaveAttribute}
                onDeleteAttribute={handleDeleteAttribute}
                initialHp={gameConfig?.initial_hp ?? 20}
                onSaveInitialHp={handleSaveInitialHp}
              />
            )}

            {systemSegment === 'items' && (
              <ItemsTab
                tableId={realTableId || ''}
                items={gameConfig?.items || []}
                isGameMaster={isGameMaster}
                onItemsChange={reloadGameConfig}
                onSaveItem={handleSaveItem}
                onDeleteItem={handleDeleteItem}
              />
            )}

            {systemSegment === 'abilities' && (
              <AbilitiesTab
                tableId={realTableId || ''}
                abilities={gameConfig?.abilities || []}
                isGameMaster={isGameMaster}
                onAbilitiesChange={reloadGameConfig}
                onSaveAbility={handleSaveAbility}
                onDeleteAbility={handleDeleteAbility}
              />
            )}
          </CollapsibleSection>
        </>
      )}

      {/* Personagens */}
      <Card id="section-characters" className="rounded-xl">
        <CardHeader className="p-4 sm:p-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle className="text-lg font-black uppercase italic tracking-tighter sm:text-xl">
                Personagens da campanha
              </CardTitle>
              <CardDescription className="font-medium">
                Gerencie os heróis e NPCs da sua jornada
              </CardDescription>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              {/* Interruptor de criação (estilo Roll20): só o mestre vê e alterna.
                  Livre = alunos criam sozinhos; mediada = o mestre conduz a criação. */}
              {isTableMaster && (
                <div className="flex flex-col gap-1">
                  <label className="flex items-center gap-2 text-sm font-medium">
                    <Switch checked={creationOpen} onCheckedChange={handleToggleCreation} />
                    <span className="whitespace-nowrap">
                      {creationOpen ? 'Criação livre' : 'Criação mediada'}
                    </span>
                  </label>
                  {!creationOpen && (
                    <span className="text-xs text-muted-foreground">
                      Alunos recebem personagem por sorteio, na sessão de jogo.
                      Você cria quando quiser (inclusive NPCs).
                    </span>
                  )}
                </div>
              )}
              {/* Mestre cria quando quiser (inclusive NPCs) — decisão do Paulo,
                  2026-07-20. Em mediada, só o ALUNO fica sem botão: o personagem
                  dele nasce no sorteio, dentro da sessão. Em livre, aluno que já
                  tem herói não vê o botão (edita pelo card). */}
              {(isGameMaster || (creationOpen && !characters.some(c => user && c.userId === user.id && !c.isNpc))) && (
                <Button
                  onClick={() => setShowCreateCharacter(true)}
                  className="group w-full font-bold uppercase tracking-wide sm:w-auto"
                >
                  <Plus className="mr-2 h-4 w-4 transition-transform group-hover:rotate-90" />
                  Criar personagem
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="px-4 pb-4 sm:px-6 sm:pb-6">
          {/* Com a mesa mediada, o sorteio acontece na sessão de jogo (lista de
              jogadores) — aqui fica só a dica para o mestre. */}
          {isTableMaster && !creationOpen && (
            <p className="mb-4 rounded-lg border border-dashed border-primary/40 bg-primary/5 p-3 text-xs text-muted-foreground">
              Criação mediada ativa: entre na sessão de jogo e sorteie os
              personagens na lista de jogadores (aba Membros).
            </p>
          )}
          {characters.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-border bg-section px-4 py-10 text-center">
              <div className="mb-4 rounded-full border border-border bg-card p-5 shadow-sm">
                <User className="h-10 w-10 text-muted-foreground" />
              </div>
              <h4 className="mb-1 text-lg font-black uppercase italic tracking-tighter">Nenhum herói convocado</h4>
              {(isTableMaster || creationOpen) ? (
                <>
                  <p className="mb-6 max-w-sm font-medium text-muted-foreground">
                    Toda saga épica começa com um herói. Crie o primeiro para iniciar esta aventura.
                  </p>
                  <Button onClick={() => setShowCreateCharacter(true)} className="font-bold uppercase tracking-wide">
                    <Plus className="mr-2 h-4 w-4" />
                    Começar agora
                  </Button>
                </>
              ) : (
                <p className="max-w-sm font-medium text-muted-foreground">
                  Os personagens desta mesa nascem por sorteio, conduzido pelo
                  mestre dentro da sessão de jogo. Entre no jogo e aguarde a sua vez.
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {characters.map((character) => {
                const membersByUserId = new Map<string, Member>()
                members.forEach(member => {
                  membersByUserId.set(member.userId, member)
                })

                const characterType = getCharacterType(character, membersByUserId)
                const typeStyle = getCharacterTypeStyle(characterType)
                const TypeIcon = typeStyle.icon

                return (
                  <div
                    key={character.id}
                    onClick={() => handleShowCharacterDetails(character)}
                    className="group cursor-pointer rounded-lg border border-border bg-section p-3 transition-all duration-200 hover:border-primary/30 hover:bg-accent/50 sm:p-4"
                  >
                    <div className="flex items-center gap-3 sm:gap-4">
                      <Avatar className="h-10 w-10 shrink-0 border-2 border-border transition-colors group-hover:border-primary/50 sm:h-12 sm:w-12">
                        <AvatarImage src={character.imageUrl} alt={character.name} />
                        <AvatarFallback className="bg-card text-sm font-black">
                          {character.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>

                      <div className="min-w-0 flex-1">
                        <div className="mb-1 flex items-center gap-2">
                          <h4 className="truncate text-sm font-black uppercase italic tracking-tighter transition-colors group-hover:text-primary">
                            {character.name}
                          </h4>
                          <Badge className={`flex h-4 items-center gap-1 border-none px-2 py-0 text-[9px] font-bold uppercase tracking-widest ${typeStyle.badge}`}>
                            <TypeIcon className="h-2.5 w-2.5" />
                          </Badge>
                        </div>
                        <p className="truncate text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                          {character.className} · Nvl {character.level}
                        </p>
                        {/* HP no mobile: linha própria sob o nome (a coluna fixa estourava 360px) */}
                        <div className="mt-2 sm:hidden">
                          <CharacterHpBar current={character.hpCurrent} total={character.hpTotal} />
                        </div>
                      </div>

                      {/* HP no desktop: coluna fixa */}
                      <div className="hidden w-24 shrink-0 sm:block">
                        <CharacterHpBar current={character.hpCurrent} total={character.hpTotal} />
                      </div>

                      {/* Sem menu "..." aqui: o trigger do dropdown abre no
                          pointerdown e disparava ao rolar a lista no celular.
                          Editar/Excluir vivem dentro do sheet de detalhes. */}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Seção de Módulos Ativos — apenas para mestre/criador. Para o aluno,
          essa seção só traria texto informativo; o acesso real (ex.: livro)
          é o botão "Entrar no livro" no topo da mesa. */}
      {isGameMaster && realTableId && (
        <TableModulesSection
          tableId={realTableId}
          isGameMaster={isGameMaster}
          refreshKey={moduleRefreshKey}
        />
      )}


            </div>{/* End MAIN CONTENT (8 cols) */}

            {/* ===== SIDEBAR (4 cols) ===== */}
            <div className="space-y-6 lg:col-span-4">

              {/* Código de acesso mora no hero (2026-07-22) */}
              {/* Estatísticas */}
              <Card className="rounded-xl">
                <CardHeader className="p-4 pb-2 sm:p-6 sm:pb-3">
                  <CardTitle className="text-xs font-black uppercase tracking-widest text-muted-foreground">
                    Estatísticas
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
                  <div className="space-y-2">
                    {[
                      { label: 'Membros', value: stats.membersCount, icon: Users },
                      { label: 'Classes', value: stats.classesCount, icon: Sparkles },
                      { label: 'Personagens', value: stats.charactersCount, icon: User },
                      { label: 'Nível médio', value: (stats.averageLevel || 0).toFixed(1), icon: TrendingUp }
                    ].map((stat) => {
                      const StatIcon = stat.icon
                      return (
                        <div key={stat.label} className="flex items-center justify-between rounded-md border border-border bg-section p-3">
                          <div className="flex items-center gap-3">
                            <StatIcon className="h-4 w-4 text-primary" />
                            <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{stat.label}</span>
                          </div>
                          <span className="text-lg font-black italic">{stat.value}</span>
                        </div>
                      )
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Membros */}
              <Card className="rounded-xl">
                <CardHeader className="p-4 pb-2 sm:p-6 sm:pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-xs font-black uppercase tracking-widest text-muted-foreground">
                      Membros
                    </CardTitle>
                    <Badge variant="outline" className="text-[10px] font-bold uppercase tracking-widest">
                      {members.length}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
                  <div className="space-y-2">
                    {members.map((member) => (
                      <div
                        key={member.id}
                        className="flex items-center gap-3 rounded-md border border-border bg-section p-3 transition-colors hover:border-primary/30"
                      >
                        <Avatar className="h-8 w-8 shrink-0 border border-border">
                          <AvatarFallback className="bg-card text-xs font-black">
                            {member.userFullName.split(' ').map(n => n[0]).join('').toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-bold">{member.userFullName}</p>
                        </div>
                        <Badge className={cn(
                          'h-5 rounded-full border-none px-2 py-0 text-[9px] font-black uppercase tracking-widest',
                          member.role === 'mestre'
                            ? 'bg-admin text-white'
                            : 'bg-accent text-accent-foreground'
                        )}>
                          {member.role === 'mestre' ? 'Mestre' : 'Jogador'}
                        </Badge>
                        {/* Botao direto (sem dropdown de item unico): o trigger
                            do menu abria no pointerdown durante a rolagem. */}
                        {isGameMaster && member.userId !== user?.id && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              if (window.confirm(`Remover ${member.userFullName} da campanha?`)) {
                                handleRemoveMember(member.id)
                              }
                            }}
                            className="h-6 w-6 shrink-0 text-muted-foreground hover:bg-error-bg hover:text-error"
                            title={`Remover ${member.userFullName}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    ))}
                    {members.length === 0 && (
                      <p className="py-4 text-center text-xs text-muted-foreground">Nenhum membro ainda</p>
                    )}
                  </div>
                </CardContent>
              </Card>

            </div>{/* End SIDEBAR (4 cols) */}

      {/* QR code de convite: mesma abordagem do module_lab (api.qrserver.com,
          sem dependência local). O QR codifica o link /convite/{code}. */}
      <Dialog open={showInviteQr} onOpenChange={setShowInviteQr}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Convite por QR code</DialogTitle>
            <DialogDescription>
              A turma aponta a câmera e entra direto na campanha (após o login).
            </DialogDescription>
          </DialogHeader>
          {inviteLink && (
            <div className="flex flex-col items-center gap-4">
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=480x480&margin=2&data=${encodeURIComponent(inviteLink)}`}
                alt={`QR code de convite da campanha (código ${table?.access_code})`}
                className="aspect-square w-full max-w-[280px] rounded-lg border border-border bg-white p-2"
              />
              <div className="w-full whitespace-pre-line break-words rounded-md bg-section p-3 text-left text-xs leading-relaxed text-muted-foreground">
                {inviteMessage}
              </div>
              <Button onClick={handleCopyInviteLink} variant="outline" className="w-full text-xs font-black uppercase tracking-widest">
                <Link2 className="mr-2 h-3.5 w-3.5" />
                Copiar convite
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <CreateCharacterDialog
        open={showCreateCharacter}
        onOpenChange={(open) => {
          setShowCreateCharacter(open)
          // Se o usuário fechar sem criar, descarta a intenção de entrar
          // (evita navegar para jogo/livro numa criação avulsa posterior).
          if (!open) {
            setPendingGameEntry(false)
            setPendingBookEntry(false)
          }
        }}
        onCreateCharacter={handleCreateCharacter}
        tableId={realTableId || ''}
        isMaster={user ? members.some(m => m.userId === user.id && m.role === 'mestre') : false}
      />

      {editingCharacter && (
        <EditCharacterDialog
          open={showEditCharacter}
          onOpenChange={(open) => {
            setShowEditCharacter(open)
            if (!open) {
              setEditingCharacter(null)
            }
          }}
          onEditCharacter={handleEditCharacter}
          character={editingCharacter}
          tableId={realTableId || ''}
          isMaster={isTableMaster}
          classeLocked={!creationOpen && !isTableMaster}
        />
      )}

      {/* Bottom Sheet de Detalhes do Personagem */}
      {selectedCharacterForDetails && (() => {
        // Criar mapa de membros por userId para passar ao CharacterDetailsSheet
        const membersByUserIdMap = new Map<string, Member>()
        members.forEach(member => {
          membersByUserIdMap.set(member.userId, member)
        })

        return (
          <CharacterDetailsSheet
            character={selectedCharacterForDetails}
            open={showCharacterDetails}
            onOpenChange={setShowCharacterDetails}
            membersByUserId={membersByUserIdMap}
            gameConfig={gameConfig}
            canEdit={canEditCharacter(selectedCharacterForDetails)}
            // Fechar o sheet ANTES de abrir dialog/confirm, com folga para a
            // animacao de saida: dois overlays Radix trocando de lugar no
            // mesmo tick ja congelou a pagina no mobile (corrida de portais).
            onEdit={() => {
              const char = selectedCharacterForDetails
              setShowCharacterDetails(false)
              setTimeout(() => handleOpenEditCharacter(char), 200)
            }}
            onDelete={() => {
              const charId = selectedCharacterForDetails.id
              setShowCharacterDetails(false)
              setTimeout(() => handleDeleteCharacter(charId), 200)
            }}
          />
        )
      })()}

      {/* Modal de Gerenciamento de Módulos */}
      {realTableId && (
        <ModuleManagerDialog
          tableId={realTableId}
          isOpen={showModuleManager}
          onClose={() => setShowModuleManager(false)}
          onModuleActivated={() => {
            // Callback quando módulo é ativado
            toast.success('Módulo ativado com sucesso!')
            setModuleRefreshKey(prev => prev + 1)
            setShowModuleManager(false)
          }}
        />
      )}

          </div>{/* End space-y-space-8 */}
        </div>{/* End max-w-7xl */}
      </div>{/* End CONTENT AREA */}
    </div>
  )
}
